import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wallet as WalletIcon, CheckCircle2, User, Receipt, Search, ArrowUpDown, Boxes, ChevronDown, Package2, CalendarDays } from "lucide-react";
import { DebtPaymentDialog } from "@/components/debts/debt-payment-dialog";
import { PageContainer } from "@/components/layout/PageContainer";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocalization } from "@/lib/localization";
import { formatOperatingCurrency } from "@/lib/money";
import { useWorkspace } from "@/lib/store";
import { toast } from "sonner";

function formatCurrencyLocal(value, locale) {
  return formatOperatingCurrency(value, locale);
}

function formatDueDateLocal(value, locale) {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  }).format(new Date(value));
}

function formatDateTimeLocal(value, locale) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function isDebtPaid(status) {
  return status === "Paid";
}

function isDebtOpen(status) {
  return status === "Unpaid" || status === "Partly paid";
}

export default function Debts() {
  const PAGE_SIZE = 40;
  const { debts, debtPayments, recordDebtPayment } = useWorkspace();
  const { locale, messages } = useLocalization();
  const copy = messages.debts;
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [view, setView] = useState(searchParams.get("view") || "debts");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [payTarget, setPayTarget] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const base = filter === "all"
      ? debts
      : filter === "unpaid"
        ? debts.filter((d) => isDebtOpen(d.status))
        : debts.filter((d) => isDebtPaid(d.status));
    const search = query.trim().toLowerCase();
    if (!search) return base;
    return base.filter((debt) => (
      debt.customerName.toLowerCase().includes(search) ||
      String(debt.phone || "").toLowerCase().includes(search) ||
      String(debt.itemSummary || "").toLowerCase().includes(search)
    ));
  }, [debts, filter, query]);
  const filteredPayments = useMemo(() => {
    const search = query.trim().toLowerCase();
    const payments = debtPayments || [];
    if (!search) return payments;
    return payments.filter((payment) => (
      payment.customerName.toLowerCase().includes(search) ||
      String(payment.debtId || "").toLowerCase().includes(search)
    ));
  }, [debtPayments, query]);

  const totals = useMemo(() => ({
    outstanding: debts.reduce((a, d) => a + (d.amountOwed - d.amountPaid), 0),
    open: debts.filter((d) => !isDebtPaid(d.status)).length,
    paid: debts.filter((d) => isDebtPaid(d.status)).length,
    overdue: debts.filter((d) => !isDebtPaid(d.status) && d.dueDate && new Date(d.dueDate) < new Date()).length,
  }), [debts]);
  const counts = useMemo(() => ({
    all: debts.length,
    unpaid: debts.filter((d) => isDebtOpen(d.status)).length,
    paid: debts.filter((d) => isDebtPaid(d.status)).length,
  }), [debts]);
  const activeRows = view === "payments" ? filteredPayments : filtered;
  const totalPages = Math.max(1, Math.ceil(activeRows.length / PAGE_SIZE));
  const paginatedDebts = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);
  const paginatedPayments = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredPayments.slice(start, start + PAGE_SIZE);
  }, [filteredPayments, page]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (filter && filter !== "all") nextParams.set("filter", filter);
    if (view !== "debts") nextParams.set("view", view);
    if (query) nextParams.set("q", query);
    setSearchParams(nextParams, { replace: true });
  }, [filter, query, setSearchParams, view]);

  useEffect(() => {
    setPage(1);
  }, [filter, query, view]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const recordPayment = async (amt) => {
    if (!payTarget || amt <= 0 || isRecording) return;
    setIsRecording(true);
    try {
      await recordDebtPayment(payTarget, amt);
      toast.success(copy.paymentRecorded(formatCurrencyLocal(amt, locale)));
      setPayTarget(null);
    } catch (error) {
      toast.error(copy.recordPaymentFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    } finally {
      setIsRecording(false);
    }
  };

  const markPaid = async (debt) => {
    const balance = Math.max(0, debt.amountOwed - debt.amountPaid);
    if (balance <= 0) return;
    try {
      await recordDebtPayment(debt, balance);
      toast.success(copy.markPaidSuccess(debt.customerName));
    } catch (error) {
      toast.error(copy.markPaidFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    }
  };

  return (
    <PageContainer>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{copy.debtsTitle}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {copy.subtitle(totals.open, formatCurrencyLocal(totals.outstanding, locale))}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="debts">{copy.debtsTab}</TabsTrigger>
              <TabsTrigger value="payments">{copy.paymentsTab}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger data-testid="debts-filter-all" value="all">{copy.all}</TabsTrigger>
              <TabsTrigger data-testid="debts-filter-unpaid" value="unpaid">{copy.unpaid}</TabsTrigger>
              <TabsTrigger data-testid="debts-filter-paid" value="paid">{copy.paid}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <DebtFilterPill active={filter === "all"} onClick={() => setFilter("all")}>
          {copy.all} <span className="font-mono ml-1">{counts.all}</span>
        </DebtFilterPill>
        <DebtFilterPill active={filter === "unpaid"} onClick={() => setFilter("unpaid")} tone="warning">
          {copy.unpaid} <span className="font-mono ml-1">{counts.unpaid}</span>
        </DebtFilterPill>
        <DebtFilterPill active={filter === "paid"} onClick={() => setFilter("paid")} tone="success">
          {copy.paid} <span className="font-mono ml-1">{counts.paid}</span>
        </DebtFilterPill>
        <DebtFilterPill active={totals.overdue > 0} onClick={() => setFilter("unpaid")} tone="danger">
          {copy.overdue} <span className="font-mono ml-1">{totals.overdue}</span>
        </DebtFilterPill>
      </div>

      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="debts-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={view === "payments" ? copy.searchPaymentsPlaceholder : copy.searchDebtsPlaceholder}
            className="h-10 pl-10"
          />
        </div>
      </div>

      {view === "payments" ? (
        <PaymentsTable
          copy={copy}
          formatAmount={(value) => formatCurrencyLocal(value, locale)}
          locale={locale}
          payments={paginatedPayments}
          totalRows={filteredPayments.length}
          page={page}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      ) : (
        <DebtsTable
          copy={copy}
          debts={paginatedDebts}
          formatAmount={(value) => formatCurrencyLocal(value, locale)}
          locale={locale}
          onMarkPaid={markPaid}
          onRecordPayment={(debt) => {
            setPayTarget(debt);
          }}
          page={page}
          pageSize={PAGE_SIZE}
          totalPages={totalPages}
          totalRows={filtered.length}
          onPageChange={setPage}
        />
      )}

      <DebtPaymentDialog
        debt={payTarget}
        copy={copy}
        formatAmount={(value) => formatCurrencyLocal(value, locale)}
        isRecording={isRecording}
        onClose={() => setPayTarget(null)}
        onSubmit={recordPayment}
      />
    </PageContainer>
  );
}

function DebtsTable({ copy, debts, formatAmount, locale, onMarkPaid, onPageChange, onRecordPayment, page, pageSize, totalPages, totalRows }) {
  return (
    <div className="luxe-card overflow-hidden rounded-2xl">
      <div className="grid grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_120px_130px_120px_140px_156px] items-center gap-x-3 border-b border-border/70 bg-muted/25 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <div className="flex items-center gap-1.5">{copy.customer} <ArrowUpDown className="h-3 w-3 opacity-50" /></div>
        <div>{copy.items}</div>
        <div className="text-right">{copy.due}</div>
        <div className="text-right">{copy.balance}</div>
        <div className="text-right">{copy.status}</div>
        <div className="text-right">{copy.settlement}</div>
        <div className="text-right">{copy.action}</div>
      </div>
      <div className="divide-y divide-border/60">
        {debts.map((debt) => {
          const balance = Math.max(0, debt.amountOwed - debt.amountPaid);
          const paid = isDebtPaid(debt.status);
          const isOverdue = !paid && debt.dueDate && new Date(debt.dueDate) < new Date();
          const soldItems = parseDebtItems(debt.itemSummary);
          const itemCount = soldItems.reduce((total, item) => total + item.quantity, 0);
          return (
            <Collapsible key={debt.id} className="group">
              <div
                data-testid={`debt-row-${debt.id}`}
                className="grid grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_120px_130px_120px_140px_156px] items-center gap-x-3 px-5 py-3.5 transition-colors hover:bg-accent/40"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted/70 text-muted-foreground shadow-sm ring-1 ring-border/50">
                    <User className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{debt.customerName}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground">{debt.phone || copy.noPhone}</div>
                  </div>
                </div>
                <div className="flex justify-start">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary group-data-[state=open]:border-primary/30 group-data-[state=open]:bg-primary/5 group-data-[state=open]:text-primary"
                    >
                      <Package2 className="h-3.5 w-3.5" />
                      <span>{copy.units(itemCount)}</span>
                      <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                    </button>
                  </CollapsibleTrigger>
                </div>
                <div className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
                  <CalendarDays className={`h-3.5 w-3.5 ${isOverdue ? "text-destructive" : ""}`} />
                  <span className={isOverdue ? "font-medium text-destructive" : ""}>
                    {debt.dueDate ? formatDueDateLocal(debt.dueDate, locale) : copy.none}
                  </span>
                </div>
                <div className="text-right">
                  <div className={`font-mono text-sm font-semibold tabular-nums ${balance <= 0 ? "text-success" : ""}`}>{formatAmount(balance)}</div>
                  <div className="font-mono text-[10px] text-muted-foreground tabular-nums">{formatAmount(debt.amountPaid)} {copy.paidSuffix}</div>
                </div>
                <div className="text-right">
                  <StatusBadge copy={copy} status={isOverdue ? "Overdue" : debt.status} />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 rounded-full px-3.5 text-xs shadow-sm"
                    disabled={paid}
                    onClick={() => onRecordPayment(debt)}
                  >
                    <WalletIcon className="mr-1.5 h-4 w-4" />
                    {copy.settle}
                  </Button>
                </div>
                <div className="flex justify-end border-l border-border/60 pl-3">
                  {!paid ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 rounded-full border-success/25 bg-success/10 px-3.5 text-xs text-success shadow-sm hover:border-success/35 hover:bg-success/15 hover:text-success"
                      title={copy.markPaid}
                      onClick={() => onMarkPaid(debt)}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {copy.markPaid}
                    </Button>
                  ) : (
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-8 rounded-full border-success/25 bg-gradient-to-r from-success/10 to-primary/5 px-3.5 text-xs font-semibold text-success shadow-sm transition-all hover:border-success/40 hover:from-success/15 hover:to-primary/10 hover:text-success group-data-[state=open]:border-success/40 group-data-[state=open]:bg-success/15"
                        title={copy.viewDetails}
                      >
                        <Receipt className="mr-1.5 h-3.5 w-3.5" />
                        {copy.viewDetails}
                        <ChevronDown className="ml-1.5 h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>
                  )}
                </div>
              </div>
              <CollapsibleContent className="px-5 pb-4">
                <div className="ml-12 rounded-2xl border border-border/70 bg-muted/[0.18] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]">
                  {paid ? (
                    <div className="mb-4 flex items-center justify-between gap-4 rounded-xl border border-success/15 bg-gradient-to-r from-success/10 via-success/5 to-transparent px-3.5 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success text-white shadow-sm">
                          <CheckCircle2 className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-success">{copy.statusPaid}</div>
                          <div className="text-[11px] text-muted-foreground">{copy.paymentReceived}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm font-semibold tabular-nums text-success">{formatAmount(debt.amountPaid)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{copy.settlement}</div>
                      </div>
                    </div>
                  ) : null}
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-foreground">{copy.soldItems}</div>
                      <div className="text-xs text-muted-foreground">{copy.soldItemsDescription}</div>
                    </div>
                    <div className="rounded-full bg-background px-3 py-1 text-[11px] font-medium text-muted-foreground shadow-sm ring-1 ring-border/60">
                      {copy.totalItems(itemCount)}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    {soldItems.map((item, index) => (
                      <div
                        key={`${debt.id}-${item.name}-${index}`}
                        className="flex items-center justify-between rounded-xl bg-background/90 px-3 py-2.5 shadow-sm ring-1 ring-border/50"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-foreground">{item.name}</div>
                          <div className="text-[11px] text-muted-foreground">{copy.originalCreditSale}</div>
                        </div>
                        <div className="rounded-full bg-primary/8 px-3 py-1 text-xs font-semibold text-primary">
                          {copy.units(item.quantity)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
        {totalRows === 0 ? (
          <div className="px-5 py-16 text-center">
            <Boxes className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <div className="text-sm text-muted-foreground">{copy.noDebtsMatch}</div>
          </div>
        ) : null}
      </div>
      <PaginationFooter
        copy={copy}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalRows={totalRows}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function PaymentsTable({ copy, formatAmount, locale, onPageChange, page, pageSize, payments, totalPages, totalRows }) {
  return (
    <div className="luxe-card overflow-hidden rounded-2xl">
      <div className="grid grid-cols-[minmax(0,1fr)_180px_180px_140px_160px] items-center border-b border-border/70 bg-muted/25 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <div className="flex items-center gap-1.5">{copy.customer} <ArrowUpDown className="h-3 w-3 opacity-50" /></div>
        <div>{copy.debtId}</div>
        <div>{copy.date}</div>
        <div className="text-right">{copy.amount}</div>
        <div className="text-right">{copy.action}</div>
      </div>
      <div className="divide-y divide-border/60">
        {payments.map((payment) => (
          <div
            key={payment.id}
            className="grid grid-cols-[minmax(0,1fr)_180px_180px_140px_160px] items-center px-5 py-3.5 transition-colors hover:bg-accent/40"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-success/10 text-success">
                <Receipt className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{payment.customerName}</div>
                <div className="truncate text-xs text-muted-foreground">{copy.paymentReceived}</div>
              </div>
            </div>
            <div className="truncate font-mono text-xs text-muted-foreground">{payment.debtId}</div>
            <div className="text-xs text-muted-foreground">{formatDateTimeLocal(payment.createdAt, locale)}</div>
            <div className="text-right font-mono text-sm font-semibold tabular-nums">{formatAmount(payment.amount)}</div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-primary/15 bg-primary/5 px-4 text-primary shadow-sm hover:border-primary/30 hover:bg-primary/10"
                onClick={() => {
                  toast.info(copy.paymentDetails, {
                    description: copy.paymentDetailsDescription(payment.customerName, formatAmount(payment.amount)),
                  });
                }}
              >
                {copy.viewDetails}
              </Button>
            </div>
          </div>
        ))}
        {totalRows === 0 ? (
          <div className="px-5 py-16 text-center">
            <Boxes className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <div className="text-sm text-muted-foreground">{copy.noDebtPaymentsMatch}</div>
          </div>
        ) : null}
      </div>
      <PaginationFooter
        copy={copy}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalRows={totalRows}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function PaginationFooter({ copy, onPageChange, page, pageSize, totalPages, totalRows }) {
  if (totalRows === 0) return null;
  return (
    <div className="flex items-center justify-between border-t border-border/70 bg-muted/10 px-5 py-3">
      <div className="text-sm text-muted-foreground">
        {copy.showing(
          Math.min((page - 1) * pageSize + 1, totalRows),
          Math.min(page * pageSize, totalRows),
          totalRows
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          disabled={page === 1}
          onClick={() => onPageChange((current) => Math.max(1, current - 1))}
        >
          {copy.previous}
        </Button>
        <div className="min-w-[68px] text-center text-sm font-medium text-foreground">
          {page} / {totalPages}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          disabled={page === totalPages}
          onClick={() => onPageChange((current) => Math.min(totalPages, current + 1))}
        >
          {copy.next}
        </Button>
      </div>
    </div>
  );
}

function DebtFilterPill({ active, onClick, children, tone = "default" }) {
  const toneClass = active
    ? tone === "success" ? "border-success bg-success/10 text-success"
    : tone === "warning" ? "border-warning bg-warning/10 text-warning"
    : tone === "danger" ? "border-destructive bg-destructive/10 text-destructive"
    : "border-primary bg-primary/10 text-primary"
    : "border-border bg-card text-foreground hover:border-foreground/30";
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${toneClass}`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ copy, status }) {
  const map = {
    "Paid": "bg-success/15 text-success",
    "Partly paid": "bg-warning/15 text-warning",
    "Unpaid": "bg-destructive/15 text-destructive",
    "Overdue": "bg-destructive/15 text-destructive",
  };
  const labelMap = {
    Paid: copy.statusPaid,
    "Partly paid": copy.statusPartlyPaid,
    Unpaid: copy.statusUnpaid,
    Overdue: copy.statusOverdue,
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${map[status]}`}>{labelMap[status] || status}</span>;
}

function parseDebtItems(itemSummary) {
  return String(itemSummary || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(\d+)x\s+(.+)$/i);
      if (!match) {
        return { name: part, quantity: 1 };
      }
      return { name: match[2].trim(), quantity: Number(match[1]) || 1 };
    });
}
